import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSite } from '../../data/SiteContext';
import {
  listHotels, countHotels, saveHotel, setHotelsVisible, listCityMappings, addCityMapping,
  removeCityMapping, getSyncStatus, runSyncNow, marginSource, money,
} from '../../data/hotels';
import HotelPanel from './HotelPanel';

const SUBTABS = [['hotels', 'Hotels'], ['cities', 'Destinations & cities'], ['sync', 'Supplier sync']];

const srcStyle = {
  hotel:  { background:'rgba(169,118,26,0.16)', color:'#a9761a' },
  dest:   { background:'rgba(14,138,156,0.14)', color:'#0c6d7c' },
  agency: { background:'rgba(90,114,111,0.14)', color:'#5a726f' },
};
const chip = (on) => ({
  padding:'8px 13px', borderRadius:11, fontSize:12.5, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap',
  color: on ? '#fff' : '#5a726f', border:'1px solid ' + (on ? 'transparent' : '#f2e2d2'),
  background: on ? 'linear-gradient(135deg,#26c6a3,#0e8a9c)' : 'rgba(255,255,255,0.85)',
});
const Box = ({ on }) => (
  <span style={{ width:16, height:16, borderRadius:4, flexShrink:0, display:'inline-block', position:'relative',
    border: on ? 'none' : '1.5px solid #bcd3ce', background: on ? 'linear-gradient(135deg,#26c6a3,#0e8a9c)' : '#fff' }}>
    {on && <span style={{ position:'absolute', left:5, top:1, width:4, height:9, border:'solid #fff', borderWidth:'0 2px 2px 0', transform:'rotate(45deg)' }} />}
  </span>
);

function Stat({ label, value, note, ok }) {
  return (
    <div className="glass" style={{ padding:'16px 18px', borderRadius:18 }}>
      <div style={{ fontSize:11.5, fontWeight:800, color:'#5a726f' }}>{label}</div>
      <div className="serif" style={{ fontSize:31, color:'#0e8a9c', lineHeight:1, margin:'5px 0 3px' }}>{value}</div>
      <div style={{ fontSize:12, fontWeight:700, color: ok ? '#1a7a48' : '#5a726f' }}>{note}</div>
    </div>
  );
}

export default function Hotels() {
  const { dests, agency } = useSite();
  const [tab, setTab] = useState('hotels');
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  // filters
  const [q, setQ] = useState('');
  const [city, setCity] = useState('');
  const [star, setStar] = useState('');
  const [country, setCountry] = useState('');
  const [destId, setDestId] = useState('');
  const [onlyPriced, setOnlyPriced] = useState(true);
  const [onlyVisible, setOnlyVisible] = useState(false);
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);

  const [totals, setTotals] = useState({ all: 0, visible: 0, priced: 0 });
  const [matching, setMatching] = useState(0);   // how many rows the current filter has, not just the page
  const [query, setQuery] = useState('');        // debounced copy of `q`, sent to the server
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE = 200;

  const filterArgs = useMemo(() => ({
    nameContains: query || undefined,
    cityName: city || undefined,
    countryName: country || undefined,
    starCategory: star || undefined,
    destinationId: destId || undefined,
    onlyVisible: onlyVisible || undefined,
    onlyUnmapped: onlyUnmapped || undefined,
    onlyWithRates: onlyPriced || undefined,
  }), [query, city, country, star, destId, onlyVisible, onlyUnmapped, onlyPriced]);

  const load = useCallback(async () => {
    setError(null);
    setSelected([]);            // a new filter means the old selection no longer makes sense
    try {
      const [rows, count] = await Promise.all([
        listHotels({ ...filterArgs, skip: 0, take: PAGE }),
        countHotels(filterArgs),
      ]);
      setHotels(rows);
      setMatching(count);
    }
    catch (e) { setError(e.message || 'Could not load hotels.'); }
    finally { setLoading(false); }
  }, [filterArgs]);

  useEffect(() => { load(); }, [load]);

  /** Appends the next page rather than replacing, so selections and scroll position survive. */
  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const rows = await listHotels({ ...filterArgs, skip: hotels.length, take: PAGE });
      setHotels(hs => [...hs, ...rows]);
    }
    catch (e) { setError(e.message || 'Could not load more hotels.'); }
    finally { setLoadingMore(false); }
  };

  // Catalogue-wide counts for the summary tiles — independent of the filter.
  useEffect(() => {
    (async () => {
      try {
        const [all, visible, priced] = await Promise.all([
          countHotels(), countHotels({ onlyVisible: true }), countHotels({ onlyWithRates: true })]);
        setTotals({ all, visible, priced });
      } catch { /* the tiles are decorative; the list is what matters */ }
    })();
  }, [hotels.length]);

  // Typing shouldn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const cities = useMemo(() => [...new Set(hotels.map(h => h.cityName).filter(Boolean))].sort(), [hotels]);
  const stars = useMemo(() => [...new Set(hotels.map(h => h.starCategory).filter(Boolean))].sort(), [hotels]);
  const countries = useMemo(() => [...new Set(hotels.map(h => h.countryName).filter(Boolean))].sort(), [hotels]);

  // Every filter is applied server-side now, so the page is what we render.
  const shown = hotels;


  const patch = (id, fields) => setHotels(hs => hs.map(h => h.id === id ? { ...h, ...fields } : h));

  const persist = async (hotel, fields) => {
    const before = hotels.find(h => h.id === hotel.id);
    patch(hotel.id, fields);                     // optimistic, so the table stays responsive
    try {
      const saved = await saveHotel(hotel, fields);
      patch(hotel.id, saved);
    } catch (e) {
      patch(hotel.id, before);                   // put it back if the API refused
      setError(e.message || 'Could not save that change.');
    }
  };

  const bulkVisibility = async (isVisible) => {
    if (!selected.length || busy) return;
    setBusy(true);
    try { await setHotelsVisible(selected, isVisible); await load(); setSelected([]); }
    catch (e) { setError(e.message || 'Could not update those hotels.'); }
    finally { setBusy(false); }
  };

  const bulkMargin = async () => {
    if (!selected.length || busy) return;
    const raw = window.prompt('Margin for the selected hotels, in percent. Leave blank to fall back to the destination or agency default.');
    if (raw === null) return;
    const value = raw.trim() === '' ? null : Number(raw);
    if (value !== null && (Number.isNaN(value) || value < 0 || value > 500)) { setError('Margin must be a number between 0 and 500.'); return; }
    setBusy(true);
    try {
      for (const id of selected) {
        const h = hotels.find(x => x.id === id);
        if (h) await saveHotel(h, { marginPercent: value });
      }
      await load(); setSelected([]);
    } catch (e) { setError(e.message || 'Could not set those margins.'); }
    finally { setBusy(false); }
  };

  const bulkDestination = async () => {
    if (!selected.length || busy) return;
    const names = dests.map((d, i) => `${i + 1}. ${d.name}`).join('\n');
    const raw = window.prompt(`Assign the selected hotels to which destination?\n\n${names}\n\nEnter a number, or blank to unassign.`);
    if (raw === null) return;
    const idx = raw.trim() === '' ? null : Number(raw) - 1;
    if (idx !== null && (Number.isNaN(idx) || !dests[idx])) { setError('That is not one of the listed destinations.'); return; }
    setBusy(true);
    try {
      for (const id of selected) {
        const h = hotels.find(x => x.id === id);
        if (h) await saveHotel(h, { destinationId: idx === null ? null : dests[idx].id });
      }
      await load(); setSelected([]);
    } catch (e) { setError(e.message || 'Could not assign those hotels.'); }
    finally { setBusy(false); }
  };

  const allShownSelected = shown.length > 0 && shown.every(h => selected.includes(h.id));

  return (
    <div style={{ display:'grid', gap:16 }}>
      <div className="glass" style={{ display:'flex', gap:4, padding:6, borderRadius:16, flexWrap:'wrap' }}>
        {SUBTABS.map(([k, label]) => (
          <span key={k} onClick={() => setTab(k)} className={'a-subtab' + (tab === k ? ' active' : '')} style={{ padding:'10px 16px' }}>{label}</span>
        ))}
      </div>

      {error && (
        <div style={{ padding:'12px 16px', borderRadius:13, background:'rgba(192,73,47,0.1)', color:'#c0492f', fontWeight:700, fontSize:13.5 }}>
          {error} <span onClick={() => setError(null)} style={{ cursor:'pointer', marginLeft:8, opacity:.7 }}>dismiss</span>
        </div>
      )}

      {tab === 'hotels' && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(158px,1fr))', gap:12 }}>
            <Stat label="In the catalogue" value={(totals.all || hotels.length).toLocaleString()} note="from the supplier feed" />
            <Stat label="Live on the site" value={totals.visible} note={totals.visible ? 'shown to customers' : 'none yet'} ok={totals.visible > 0} />
            <Stat label="Have prices" value={totals.priced} note="the rest can't be sold" />
            <Stat label="Matching filters" value={matching.toLocaleString()} note={hotels.length < matching ? `${hotels.length} loaded` : 'all loaded'} />
          </div>

          <div className="glass" style={{ display:'flex', gap:9, flexWrap:'wrap', alignItems:'center', padding:'13px 16px', borderRadius:16 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search hotels…" className="a-input" style={{ flex:1, minWidth:190, padding:'9px 12px' }} />
            <select value={city} onChange={e => setCity(e.target.value)} className="a-input" style={{ width:'auto', padding:'9px 12px' }}>
              <option value="">Any city</option>{cities.map(c => <option key={c}>{c}</option>)}
            </select>
            <select value={country} onChange={e => setCountry(e.target.value)} className="a-input" style={{ width:'auto', padding:'9px 12px' }}>
              <option value="">Any country</option>{countries.map(c => <option key={c}>{c}</option>)}
            </select>
            <select value={star} onChange={e => setStar(e.target.value)} className="a-input" style={{ width:'auto', padding:'9px 12px' }}>
              <option value="">Any rating</option>{stars.map(s => <option key={s}>{s}</option>)}
            </select>
            <select value={destId} onChange={e => setDestId(e.target.value)} className="a-input" style={{ width:'auto', padding:'9px 12px' }}>
              <option value="">Any destination</option>{dests.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <span onClick={() => setOnlyPriced(v => !v)} style={chip(onlyPriced)}>Has prices</span>
            <span onClick={() => setOnlyVisible(v => !v)} style={chip(onlyVisible)}>Live only</span>
            <span onClick={() => setOnlyUnmapped(v => !v)} style={chip(onlyUnmapped)}>Not assigned</span>
          </div>

          {selected.length > 0 && (
            <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', padding:'11px 16px', borderRadius:14, background:'linear-gradient(135deg,rgba(38,198,163,0.16),rgba(14,138,156,0.14))', border:'1px solid rgba(14,138,156,0.28)' }}>
              <b style={{ fontSize:13, color:'#0c6d7c' }}>{selected.length} selected</b>
              <button onClick={() => bulkVisibility(true)} disabled={busy} className="a-primary" style={{ padding:'9px 15px', borderRadius:11, fontSize:12.5 }}>Show on site</button>
              <button onClick={() => bulkVisibility(false)} disabled={busy} className="a-ghost" style={{ padding:'9px 15px', borderRadius:11, fontSize:12.5 }}>Hide</button>
              <button onClick={bulkDestination} disabled={busy} className="a-ghost" style={{ padding:'9px 15px', borderRadius:11, fontSize:12.5 }}>Assign to destination…</button>
              <button onClick={bulkMargin} disabled={busy} className="a-ghost" style={{ padding:'9px 15px', borderRadius:11, fontSize:12.5 }}>Set margin…</button>
              <span onClick={() => setSelected([])} style={{ cursor:'pointer', fontSize:12.5, fontWeight:700, color:'#5a726f', marginLeft:'auto' }}>Clear</span>
            </div>
          )}

          <div className="glass-card" style={{ padding:'2px 16px 10px', borderRadius:20, overflowX:'auto' }}>
            {loading ? (
              <div style={{ padding:'48px 12px', color:'#5a726f' }}>Loading hotels…</div>
            ) : shown.length === 0 ? (
              <div style={{ padding:'52px 24px', textAlign:'center' }}>
                <div className="serif" style={{ fontSize:22, marginBottom:8 }}>No hotels match those filters</div>
                <div style={{ fontSize:14, color:'#5a726f' }}>Only {totals.priced} of {totals.all.toLocaleString()} hotels are priced by the supplier — try clearing a filter.</div>
              </div>
            ) : (
              <table className="a-table" style={{ minWidth:900 }}>
                <thead>
                  <tr>
                    <th style={{ width:28 }}>
                      <span style={{ cursor:'pointer' }} onClick={() => setSelected(allShownSelected ? [] : shown.map(h => h.id))}>
                        <Box on={allShownSelected} />
                      </span>
                    </th>
                    <th>Hotel</th><th>Rating</th><th>Prices</th>
                    <th>You pay → you charge</th><th>Margin</th><th>Destination</th><th>On site</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(h => {
                    const dest = dests.find(d => d.id === h.destinationId);
                    const src = marginSource(h, dest);
                    const on = selected.includes(h.id);
                    return (
                      <tr key={h.id} style={on ? { background:'rgba(38,198,163,0.07)' } : undefined}>
                        <td>
                          <span style={{ cursor:'pointer' }} onClick={() => setSelected(s => on ? s.filter(x => x !== h.id) : [...s, h.id])}>
                            <Box on={on} />
                          </span>
                        </td>
                        <td onClick={() => setEditing(h)} style={{ cursor:'pointer' }}>
                          <div style={{ fontWeight:800 }}>{h.displayName || h.name}</div>
                          <div style={{ fontSize:11.5, color:'#5a726f', marginTop:2 }}>
                            {[h.cityName, h.regionName, h.countryName].filter(Boolean).join(' · ')} · supplier #{h.supplierHotelId}
                          </div>
                        </td>
                        <td><span style={{ padding:'4px 10px', borderRadius:100, fontSize:11, fontWeight:800, background:'rgba(14,138,122,0.12)', color:'#0c6d7c', whiteSpace:'nowrap' }}>{h.starCategory || '—'}</span></td>
                        <td style={{ fontVariantNumeric:'tabular-nums' }}>{h.rateCount ? h.rateCount.toLocaleString() : 0}</td>
                        <td>
                          {h.supplierFrom != null ? (
                            <span style={{ display:'flex', alignItems:'baseline', gap:7, fontVariantNumeric:'tabular-nums' }}>
                              <span style={{ color:'#5a726f', fontSize:12.5 }}>{money(h.supplierFrom)}</span>
                              <span style={{ color:'rgba(12,43,47,0.3)', fontSize:11 }}>→</span>
                              <span style={{ fontWeight:800, fontSize:15 }}>{money(h.sellFrom)}</span>
                            </span>
                          ) : <span style={{ fontSize:12.5, color:'#5a726f' }}>No prices from the supplier</span>}
                        </td>
                        <td>
                          {h.supplierFrom != null ? (
                            <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <input
                                type="number" min="0" max="500"
                                defaultValue={h.marginPercent ?? ''}
                                placeholder={String(h.appliedMarginPercent ?? '')}
                                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                onBlur={e => {
                                  const raw = e.target.value.trim();
                                  const next = raw === '' ? null : Number(raw);
                                  if (next !== null && (Number.isNaN(next) || next < 0 || next > 500)) { setError('Margin must be between 0 and 500.'); e.target.value = h.marginPercent ?? ''; return; }
                                  if (next !== (h.marginPercent ?? null)) persist(h, { marginPercent: next });
                                }}
                                style={{ width:62, padding:'6px 8px', borderRadius:9, border:'1px solid #f2e2d2', background:'rgba(255,255,255,0.9)', fontSize:13.5, fontWeight:800, fontFamily:'inherit', fontVariantNumeric:'tabular-nums' }} />
                              <span style={{ ...srcStyle[src.kind], padding:'3px 7px', borderRadius:6, fontSize:9.5, fontWeight:800, letterSpacing:'.07em', textTransform:'uppercase', whiteSpace:'nowrap' }}>{src.label}</span>
                            </span>
                          ) : <span style={{ color:'#5a726f' }}>—</span>}
                        </td>
                        <td>
                          <span style={{ padding:'4px 10px', borderRadius:100, fontSize:11, fontWeight:800, whiteSpace:'nowrap',
                            background: dest ? 'rgba(14,138,156,0.12)' : 'rgba(90,114,111,0.13)', color: dest ? '#0c6d7c' : '#5a726f' }}>
                            {dest ? dest.name : 'Not assigned'}
                          </span>
                        </td>
                        <td>
                          <span onClick={() => persist(h, { isVisible: !h.isVisible })} title={h.supplierFrom == null ? 'This hotel has no prices, so it will not show a price on the site' : ''}
                            style={{ width:38, height:22, borderRadius:100, padding:3, cursor:'pointer', display:'flex', flexShrink:0, background: h.isVisible ? 'linear-gradient(135deg,#26c6a3,#0e8a9c)' : '#d7e6e2', justifyContent: h.isVisible ? 'flex-end' : 'flex-start' }}>
                            <span style={{ width:16, height:16, borderRadius:'50%', background:'#fff', boxShadow:'0 2px 5px rgba(6,40,45,0.3)' }}></span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {hotels.length < matching && (
            <div style={{ display:'flex', alignItems:'center', gap:12, justifyContent:'center', padding:'4px 0 2px' }}>
              <button onClick={loadMore} disabled={loadingMore} className="a-ghost"
                style={{ padding:'11px 22px', borderRadius:12, fontSize:13.5, cursor:'pointer', opacity: loadingMore ? 0.6 : 1 }}>
                {loadingMore ? 'Loading…' : `Load ${Math.min(PAGE, matching - hotels.length)} more`}
              </button>
              <span style={{ fontSize:12.5, color:'#5a726f' }}>
                showing {hotels.length.toLocaleString()} of {matching.toLocaleString()}
              </span>
            </div>
          )}

          <div style={{ fontSize:12.5, color:'#5a726f' }}>
            Margins are applied when a price is shown, so a change takes effect immediately — no need to re-sync.
            Blank follows the destination, then the agency default ({agency?.defaultMarginPercent ?? 0}%).
          </div>
        </>
      )}

      {tab === 'cities' && <CityMappings dests={dests} onError={setError} />}
      {tab === 'sync' && <SyncPanel onError={setError} />}

      {editing && (
        <HotelPanel
          hotel={editing}
          destinations={dests}
          onClose={() => setEditing(null)}
          onSave={async (v) => { await persist(v, {
            isVisible: v.isVisible, marginPercent: v.marginPercent, displayName: v.displayName,
            blurb: v.blurb, imageFileId: v.imageFileId, destinationId: v.destinationId }); }}
        />
      )}
    </div>
  );
}

/** Which supplier cities feed a destination — without a mapping, no rates are pulled for it. */
function CityMappings({ dests, onError }) {
  const [maps, setMaps] = useState([]);
  const [adding, setAdding] = useState({});

  const load = useCallback(async () => {
    try { setMaps(await listCityMappings()); } catch (e) { onError(e.message || 'Could not load the city mappings.'); }
  }, [onError]);
  useEffect(() => { load(); }, [load]);

  const add = async (destinationId) => {
    const name = (adding[destinationId] || '').trim();
    if (!name) return;
    try { await addCityMapping(destinationId, name); setAdding(a => ({ ...a, [destinationId]: '' })); await load(); }
    catch (e) { onError(e.message || 'Could not add that city.'); }
  };

  const remove = async (id) => {
    try { await removeCityMapping(id); await load(); }
    catch (e) { onError(e.message || 'Could not remove that city.'); }
  };

  return (
    <div className="glass-card" style={{ padding:22, borderRadius:20 }}>
      <div className="serif" style={{ fontSize:19, marginBottom:3 }}>Destinations and their cities</div>
      <div style={{ fontSize:12.5, color:'#5a726f', marginBottom:18, maxWidth:'70ch' }}>
        The supplier splits an area into several cities — Punta Cana is really four of them. Every city has to
        be listed here or its hotels never get prices.
      </div>

      <div style={{ display:'grid', gap:14 }}>
        {dests.map(d => {
          const mine = maps.filter(m => m.destinationId === d.id);
          return (
            <div key={d.id} style={{ padding:'14px 16px', borderRadius:14, background:'rgba(255,255,255,0.6)', border:'1px solid rgba(14,138,122,0.12)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:10 }}>
                <b style={{ fontSize:14 }}>{d.name}</b>
                {d.hotelPriceFrom != null
                  ? <span style={{ fontSize:11.5, fontWeight:800, padding:'3px 9px', borderRadius:100, background:'rgba(26,122,72,0.14)', color:'#1a7a48' }}>from {money(d.hotelPriceFrom)}</span>
                  : <span style={{ fontSize:11.5, fontWeight:800, padding:'3px 9px', borderRadius:100, background:'rgba(90,114,111,0.13)', color:'#5a726f' }}>no live prices</span>}
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                {mine.map(m => (
                  <span key={m.id} style={{ display:'inline-flex', alignItems:'center', gap:7, fontSize:11.5, fontWeight:700, padding:'4px 9px', borderRadius:8, background:'rgba(14,138,122,0.1)', color:'#0c6d7c' }}>
                    {m.supplierCityName}
                    <span onClick={() => remove(m.id)} style={{ cursor:'pointer', opacity:.65 }}>×</span>
                  </span>
                ))}
                {mine.length === 0 && <span style={{ fontSize:12, color:'#5a726f' }}>No cities yet — no prices will load.</span>}
                <input value={adding[d.id] || ''} onChange={e => setAdding(a => ({ ...a, [d.id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') add(d.id); }}
                  placeholder="Add a supplier city…" className="a-input"
                  style={{ width:200, padding:'6px 10px', fontSize:12.5, marginLeft:4 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Status of the nightly pull, plus a manual trigger. */
function SyncPanel({ onError }) {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try { setStatus(await getSyncStatus()); } catch (e) { onError(e.message || 'Could not load the sync status.'); }
  }, [onError]);

  useEffect(() => { load(); }, [load]);
  // While a run is in flight the counters move, so keep refreshing.
  useEffect(() => {
    if (!status?.isRunning) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [status?.isRunning, load]);

  const start = async () => {
    setStarting(true);
    try { await runSyncNow(); await load(); }
    catch (e) { onError(e.message || 'Could not start a sync.'); }
    finally { setStarting(false); }
  };

  const dot = (s) => ({ Succeeded:'#1a7a48', Partial:'#a9761a', Failed:'#c0492f', Running:'#0e8a9c' }[s] || '#5a726f');
  const when = (iso) => { try { return new Date(iso).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }); } catch { return iso; } };

  if (!status) return <div className="glass-card" style={{ padding:24, borderRadius:20, color:'#5a726f' }}>Loading sync status…</div>;

  return (
    <div className="glass-card" style={{ padding:22, borderRadius:20 }}>
      <div className="serif" style={{ fontSize:19, marginBottom:3 }}>Supplier sync</div>
      <div style={{ fontSize:12.5, color:'#5a726f', marginBottom:16, maxWidth:'70ch' }}>
        Runs on its own every night at {status.dailyRunAt}. The supplier can't tell us what changed, so each run
        re-reads everything and keeps what's new.
      </div>

      <div style={{ display:'flex', gap:9, alignItems:'center', flexWrap:'wrap', marginBottom:18 }}>
        <button onClick={start} disabled={starting || status.isRunning} className="a-primary"
          style={{ padding:'10px 18px', borderRadius:12, fontSize:13.5, opacity: (starting || status.isRunning) ? 0.6 : 1 }}>
          {status.isRunning ? 'Running…' : starting ? 'Starting…' : 'Sync now'}
        </button>
        <span style={chip(status.enabled)}>{status.enabled ? `Nightly at ${status.dailyRunAt}` : 'Scheduled sync is off'}</span>
        <span style={chip(false)}>{status.monthsAhead} months ahead</span>
      </div>

      {status.runs.length === 0 && <div style={{ fontSize:13.5, color:'#5a726f' }}>No runs recorded yet.</div>}

      <div style={{ display:'grid' }}>
        {status.runs.map(r => (
          <div key={r.id} style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', padding:'10px 0', borderTop:'1px solid rgba(14,138,122,0.1)', fontSize:12.5 }}>
            <span style={{ width:8, height:8, borderRadius:'50%', flexShrink:0, background:dot(r.status) }}></span>
            <b>{when(r.startedAt)}</b>
            <span style={{ color:'#5a726f' }}>{r.isManual ? 'manual' : 'automatic'}</span>
            <span style={{ color:'#5a726f' }}>{r.citiesSucceeded}/{r.citiesRequested} cities</span>
            <span style={{ marginLeft:'auto', color:'#5a726f', fontVariantNumeric:'tabular-nums' }}>
              {r.ratesSeen.toLocaleString()} prices
              {r.ratesAdded ? ` · +${r.ratesAdded.toLocaleString()}` : ''}
              {r.ratesRemoved ? ` · −${r.ratesRemoved.toLocaleString()}` : ''}
              {!r.ratesAdded && !r.ratesRemoved && r.status === 'Succeeded' ? ' · nothing changed' : ''}
            </span>
            {r.error && <div style={{ width:'100%', fontSize:12, color:'#c0492f', paddingLeft:18 }}>{r.error}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
